import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTextFontOverrides } from 'utils/font-override';
import { useCallback, useEffect, useRef } from 'react';
import { buildCssVar } from 'utils/style-override';
import { useStickyAnchor } from 'utils/sticky-anchor-context';
import { onAddToCart } from 'utils/gtm';
import * as rudder from 'utils/rudderstack';
import { type SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';
import { buildImgProps } from 'utils/media';

import type { ComponentsStickyBanner } from 'queries/graphql';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

import styles from './styles.module.scss';

const THRESHOLD_ARRAY = Array.from({ length: 20 }, (_, i) => i / 20);

export default function StickyBanner({
  sectionId,
  buttonText,
  buttonLink,
  text,
  image,
  style,
}: ComponentsStickyBanner) {
  const {
    buttonColor,
    buttonHoverColor,
    buttonTextColor,
    buttonHoverTextColor,
    buttonBorderColor,
    buttonHoverBorderColor,
    textColor,
    textFont,
    backgroundColor,
  } = style || {};

  const stickyBannerRef = useRef(null);
  const { anchorRef } = useStickyAnchor();

  useEffect(() => {
    const stickyAnchorElement = anchorRef?.current?.nextSibling;
    const stickyAnchorElementTop = stickyAnchorElement?.getBoundingClientRect().top;

    let stickyAnchorObserver;
    if (stickyAnchorElement) {
      if ('IntersectionObserver' in window) {
        stickyAnchorObserver = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              const _stickyBanner: any = stickyBannerRef.current;

              if (_stickyBanner) {
                _stickyBanner.classList.toggle(
                  styles.show,
                  entry.boundingClientRect.top >= 0 || entry.boundingClientRect.bottom <= 0
                );
              }
            });
          },
          {
            threshold: THRESHOLD_ARRAY,
            rootMargin: `${stickyAnchorElementTop}px`,
          }
        );
        stickyAnchorObserver.observe(stickyAnchorElement);
      }
    }

    return () =>
      stickyAnchorObserver &&
      stickyAnchorElement &&
      stickyAnchorObserver.unobserve(stickyAnchorElement);
  }, [anchorRef]);

  const { offersConfig } = useFunnelData<SalesFunnelData>();
  const offer = offersConfig[0];

  const handleClick = useCallback(() => {
    onAddToCart([offer]);
    rudder.onAddToCart([offer]);
  }, [offer]);

  const textFontOverrides = useTextFontOverrides(textFont);

  return (
    <div
      ref={stickyBannerRef}
      id={sectionId}
      className={`root ${styles.section}`}
      data-cy="sticky-banner"
    >
      <style jsx>{`
        .root {
          ${buildCssVar('--button-color', buttonColor, 'var(--primary-color)')}
          ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
          ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
          ${buildCssVar(
            '--button-hover-text-color',
            buttonHoverTextColor,
            'var(--text-color-light)'
          )}
          ${buildCssVar(
            '--button-border-color',
            buttonBorderColor,
            buttonColor,
            'var(--primary-color)'
          )}
          ${buildCssVar(
            '--button-hover-border-color',
            buttonHoverBorderColor,
            buttonHoverColor,
            'var(--primary-color)'
          )}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${textFontOverrides ?? ''}
        }
      `}</style>
      <div className={styles.container}>
        <div className={styles.column}>
          {image && (
            <div className={styles.image_wrapper}>
              <Image {...buildImgProps(image)} alt={image.alt} priority />
            </div>
          )}

          {text && (
            <div className={styles.text}>
              <MarkdownText text={text}></MarkdownText>
            </div>
          )}
        </div>

        <a className={styles.btn} onClick={handleClick} href={`#${buttonLink}`}>
          {buttonText}
        </a>
      </div>
    </div>
  );
}
